/* global google */

'use client';

import { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  useAutocomplete,
  useGoogleMap,
} from '@ubilabs/google-maps-react-hooks';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

import { ICoordinate } from '@northladder/services';

import { IGooglePlace } from '../../types';

interface IMapPlacesAutocompleteWidgetProps {
  onUpdateLocation: (location: ICoordinate) => void;
}

/**
 * -----------------------------------------------------------------------------
 * This allows for the user to search through the map auto-complete feature
 * for cleaner locations labels
 */
export function MapPlacesAutocompleteWidget({
  onUpdateLocation,
}: IMapPlacesAutocompleteWidgetProps) {
  const map = useGoogleMap();

  const [activeMap, setActiveMap] = useState<google.maps.Map>();

  useEffect(() => {
    if (map) {
      setActiveMap(map);
    }
  }, [map]);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const [inputValue, setInputValue] = useState('');

  const [_, setFormattedSelectedLocation] = useState<ICoordinate | null>(null);

  const handelOnPlaceChanged = (place: IGooglePlace) => {
    const geoLoc = place.geometry?.location;

    if (geoLoc) {
      const { name, formatted_address: formattedAddress } = place;

      const newLocation: ICoordinate = {
        lat: geoLoc.lat(),
        lng: geoLoc.lng(),
        zoom: activeMap?.getZoom() || 15,
        title: formattedAddress || name,
      };

      setFormattedSelectedLocation(newLocation);

      if (!formattedAddress || !name) return;

      setInputValue(formattedAddress || name);

      // Keep focus on input element
      inputRef.current?.focus();

      /**
       * Push this to the calling parent to update.
       */
      onUpdateLocation(newLocation);
    }
  };

  /**
   * Use the useAutocomplete hook and pass the input field ref and the
   * onPlaceChanged function to it
   */
  useAutocomplete({
    inputField: inputRef && inputRef.current,
    onPlaceChanged: handelOnPlaceChanged,
  });

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event?.target?.value);
  };

  return (
    <div
      className="
        absolute top-4 z-10 w-[calc(100%-2rem)] ltr:left-4 rtl:right-4
        sm:w-96
      "
    >
      <div className="relative">
        <input
          ref={inputRef}
          className="
            bg-background text-foreground border-input focus:ring-ring w-full
            rounded-lg border py-2.5 shadow-lg transition-shadow focus:outline-none
            focus:ring-2 focus:ring-offset-2 ltr:pl-10 ltr:pr-4
            rtl:pl-4 rtl:pr-10
          "
          onChange={handleInputChange}
          placeholder="Search for a location..."
          type="text"
          value={inputValue}
        />
        <MagnifyingGlassIcon
          className="
            text-muted-foreground absolute top-1/2 h-5 w-5
            -translate-y-1/2 ltr:left-3 rtl:right-3
          "
        />
      </div>
    </div>
  );
}
